import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import {
  SEARCH_THRESHOLD, MENU_CLASS, optionClass, matchOption,
  useAnchoredPosition, useOutsideClose, useListboxKeys, useScrollActiveIntoView,
} from "./dropdownKit";

/**
 * Single-select thay <select> native — theo pattern dropdown của HRM Hub:
 * trigger có viền + nhãn đã chọn + chevron, danh sách nổi render qua portal,
 * có ô tìm kiếm từ 5 lựa chọn trở lên, dòng đã chọn tô nhạt accent kèm dấu check.
 *
 * Bàn phím: Enter/Space/↓ mở, ↑↓ chạy dòng, Enter chọn, Esc đóng (focus quay về
 * trigger), Tab thoát. Trigger mang semantics combobox, danh sách là listbox.
 *
 * Props: value, options [{value,label,disabled,icon?}], onChange(value), placeholder,
 *        allowClear (hiện mục trống để bỏ chọn — chỉ bật khi field không bắt buộc).
 */
const SelectDropdown = ({
  value, options = [], onChange, placeholder = "Select...",
  allowClear = false, clearLabel = "— None —", disabled = false, 'aria-label': ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);

  const selected = options.find((o) => o.value === value);
  const showSearch = options.length >= SEARCH_THRESHOLD;

  const filtered = useMemo(
    () => (showSearch ? options.filter((o) => matchOption(o, query)) : options),
    [options, query, showSearch]
  );

  // Mục "bỏ chọn" đứng đầu danh sách, chỉ hiện khi không đang tìm kiếm.
  const rows = useMemo(
    () => (allowClear && !query.trim()
      ? [{ value: "", label: clearLabel, __clear: true }, ...filtered]
      : filtered),
    [allowClear, clearLabel, filtered, query]
  );

  const close = () => { setOpen(false); setQuery(""); };
  useOutsideClose(open, [btnRef, popRef], close);

  const pick = (idx) => {
    const row = rows[idx];
    if (!row || row.disabled) return;
    onChange(row.__clear ? "" : row.value);
    setQuery(""); setOpen(false);
    btnRef.current?.focus();
  };

  const { activeIndex, setActiveIndex, onKeyDown } = useListboxKeys({
    open, setOpen, count: rows.length, onSelect: pick, anchorRef: btnRef,
  });
  useScrollActiveIntoView(listRef, activeIndex);

  useEffect(() => {
    if (!open || !showSearch) return undefined;
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open, showSearch]);

  const coords = useAnchoredPosition(open, btnRef, { minWidth: 200 });
  const listId = "pr-select-list";

  return (
    <>
      <div
        ref={btnRef}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-controls={open ? listId : undefined}
        aria-disabled={disabled || undefined}
        onKeyDown={disabled ? undefined : onKeyDown}
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        className={`tw-w-full tw-h-11 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-geist-sm tw-border tw-border-solid tw-px-3 tw-text-sm tw-select-none tw-transition-colors ${
          disabled
            ? "tw-bg-geist-bg-2 tw-border-geist-border tw-text-geist-fg-3 tw-cursor-not-allowed"
            : `tw-cursor-pointer tw-bg-geist-bg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-geist-accent/20 ${
              open ? "tw-border-geist-accent" : "tw-border-geist-border hover:tw-border-geist-border-2"}`
        }`}
      >
        <span className={`tw-flex tw-items-center tw-gap-2 tw-min-w-0 ${selected ? "tw-text-geist-fg" : "tw-text-geist-fg-3"}`}>
          {selected?.icon && <span className="tw-flex-shrink-0">{selected.icon}</span>}
          <span className="tw-truncate">{selected ? selected.label : placeholder}</span>
        </span>
        <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"} tw-text-[11px] tw-text-geist-fg-3 tw-flex-shrink-0`} />
      </div>

      {open && coords && createPortal(
        <div
          ref={popRef}
          style={{ position: "fixed", zIndex: 100060, ...coords, maxHeight: undefined, width: coords.width }}
          className={MENU_CLASS}
          onKeyDown={onKeyDown}
          role="presentation"
        >
          {showSearch && (
            <div className="tw-p-2 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border tw-bg-geist-bg-2">
              <div className="tw-flex tw-items-center tw-gap-2 tw-bg-geist-bg tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-sm tw-px-2.5 tw-py-1.5 focus-within:tw-border-geist-accent">
                <i className="bi bi-search tw-text-[11px] tw-text-geist-fg-3 tw-flex-shrink-0" />
                <input
                  ref={searchRef}
                  autoComplete="off"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
                  placeholder="Search..."
                  className="tw-flex-1 tw-min-w-0 tw-bg-transparent tw-border-0 tw-outline-none tw-text-sm tw-text-geist-fg placeholder:tw-text-geist-fg-3"
                />
                {query && (
                  <i role="button" tabIndex={-1} title="Clear search" onClick={() => { setQuery(""); searchRef.current?.focus(); }}
                    className="bi bi-x-lg tw-text-[10px] tw-text-geist-fg-3 hover:tw-text-geist-fg tw-cursor-pointer" />
                )}
              </div>
            </div>
          )}

          <div ref={listRef} id={listId} role="listbox" style={{ maxHeight: coords.maxHeight }}
            className="tw-overflow-y-auto tw-py-1">
            {rows.length === 0 ? (
              <div className="tw-px-3 tw-py-4 tw-text-center tw-text-sm tw-text-geist-fg-3">No matching option.</div>
            ) : rows.map((o, idx) => {
              const isSelected = o.__clear ? !value : o.value === value;
              return (
                <div
                  key={o.__clear ? "__clear" : o.value}
                  data-index={idx}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={o.disabled || undefined}
                  tabIndex={-1}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => pick(idx)}
                  title={o.label}
                  className={`${optionClass(isSelected, activeIndex === idx)} ${o.disabled ? "tw-opacity-40 tw-cursor-not-allowed" : ""} ${o.__clear ? "tw-italic" : ""}`}
                >
                  {isSelected
                    ? <i className="bi bi-check2 tw-text-[13px] tw-text-geist-accent tw-flex-shrink-0" />
                    : <span className="tw-w-[13px] tw-flex-shrink-0" />}
                  {o.icon && <span className="tw-flex-shrink-0">{o.icon}</span>}
                  <span className="tw-truncate">{o.label}</span>
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const optionValueType = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

SelectDropdown.propTypes = {
  value: optionValueType,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: optionValueType,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    icon: PropTypes.node,
  })),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  allowClear: PropTypes.bool,
  clearLabel: PropTypes.string,
  disabled: PropTypes.bool,
  'aria-label': PropTypes.string,
};

export default SelectDropdown;
